/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { hooks as commonHooks } from '@sitebuilder/common';
import { makeRequest } from '../utils';
import { DownloadBookletOptions } from './CustomizedFundList';

export interface DownloadBookletProps extends DownloadBookletOptions {
    data: any;
    storageName: string;
    selectionList: string[];
}

const DownloadBooklet = (props: DownloadBookletProps): JSX.Element => {
    const { isClient } = commonHooks.useAppContext();
    const { data, downloadBookletButtonText, showDownloadBookletIcon, storageName, ...rest } =
        props;
    const i18n = commonHooks.useLocalization();
    const [packageURL, setPackageURL] = useState<string>('');
    const [requestState, setRequestState] = useState<string>('');

    const baseURL = `/api/package`;
    const apiBaseURL =
        isClient && window?.location?.hostname === 'localhost'
            ? `http://localhost:3001/api/package`
            : baseURL;

    // Render Download Button Icon
    const renderIcon = (
        position?: string,
        type?: 'FontAwesome Icon' | 'Image',
        iconName?: string,
        imageUrl?: string,
        className?: string
    ) => {
        return (
            <span className={`${className || ''} icon-${position}`}>
                {type === 'FontAwesome Icon' ? (
                    <i className={iconName} />
                ) : (
                    <img
                        className="download-booklet-image"
                        src={imageUrl}
                        alt={'download-booklet-icon'}
                    />
                )}
            </span>
        );
    };

    const onDownloadAllClick = async () => {
        const links = rest?.selectionList || [];
        if (links?.length === 0) {
            return;
        }

        const body = { pdfUrls: links };

        setRequestState('PENDING');

        makeRequest(`${baseURL}/publish`, 'POST', JSON.stringify(body))
            .then((id) => {
                let hasDownloadedPackage = false;
                const statusInterval = setInterval(() => {
                    makeRequest(`${baseURL}/status?publicationId=${id}`, 'GET', null).then(
                        (data) => {
                            if (data?.status.toLowerCase() === 'completed') {
                                clearInterval(statusInterval);
                                hasDownloadedPackage = true;
                                setPackageURL(
                                    `${apiBaseURL}/download?id=${id}&filename=${rest?.bookletName}`
                                );
                                setRequestState('SUCCESS');
                            } else if (data?.status.toLowerCase() === 'failed') {
                                clearInterval(statusInterval);
                                setRequestState('FAILED');
                            }
                        }
                    );
                }, 5000);

                // Set a timeout of 10 minutes before returning an error
                setTimeout(() => {
                    clearInterval(statusInterval);

                    if (!hasDownloadedPackage) {
                        setRequestState('FAILED');
                    }
                }, 600000);
            })
            .catch((error: any) => {
                setRequestState('FAILED');
                return error;
            });
    };

    return (
        <div className={'download-booklet'}>
            <button
                className="download-booklet-button"
                data-testid="download-booklet-button"
                onClick={() => onDownloadAllClick()}
            >
                {showDownloadBookletIcon &&
                    rest?.downloadBookletIconPosition === 'Left' &&
                    renderIcon(
                        rest?.downloadBookletIconPosition,
                        rest?.downloadBookletIconType,
                        rest?.downloadBookletIconName,
                        rest?.downloadBookletImage,
                        'download-booklet-icon'
                    )}
                <span
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(downloadBookletButtonText, data),
                    }}
                />
                {showDownloadBookletIcon &&
                    rest?.downloadBookletIconPosition === 'Right' &&
                    renderIcon(
                        rest?.downloadBookletIconPosition,
                        rest?.downloadBookletIconType,
                        rest?.downloadBookletIconName,
                        rest?.downloadBookletImage,
                        'download-booklet-icon'
                    )}
            </button>
            <div className="booklet-messages-container">
                {requestState === 'PENDING' && (
                    <div className={'booklet-download-info booklet-message'}>
                        {rest?.downloadBookletMessageIcon &&
                            rest?.downloadBookletMessageIconPosition === 'Left' &&
                            renderIcon(
                                rest?.downloadBookletMessageIconPosition,
                                rest?.downloadBookletMessageIconType,
                                rest?.downloadBookletMessageIconName,
                                rest?.downloadBookletMessageImage,
                                'download-booklet-message-icon'
                            )}
                        <span
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(rest.downloadBookletMessage, data),
                            }}
                        />
                        {rest?.downloadBookletMessageIcon &&
                            rest?.downloadBookletMessageIconPosition === 'Right' &&
                            renderIcon(
                                rest?.downloadBookletMessageIconPosition,
                                rest?.downloadBookletMessageIconType,
                                rest?.downloadBookletMessageIconName,
                                rest?.downloadBookletMessageImage,
                                'download-booklet-message-icon'
                            )}
                    </div>
                )}

                {requestState === 'FAILED' && (
                    <div className="booklet-download-info booklet-error">
                        {rest?.downloadBookletErrorMessageIcon &&
                            rest?.downloadBookletErrorMessageIconPosition === 'Left' &&
                            renderIcon(
                                rest?.downloadBookletErrorMessageIconPosition,
                                rest?.downloadBookletErrorMessageIconType,
                                rest?.downloadBookletErrorMessageIconName,
                                rest?.downloadBookletErrorMessageImage,
                                'download-booklet-error-icon'
                            )}
                        <span
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(rest.downloadBookletErrorMessage, data),
                            }}
                        />
                        {rest?.downloadBookletErrorMessageIcon &&
                            rest?.downloadBookletErrorMessageIconPosition === 'Right' &&
                            renderIcon(
                                rest?.downloadBookletErrorMessageIconPosition,
                                rest?.downloadBookletErrorMessageIconType,
                                rest?.downloadBookletErrorMessageIconName,
                                rest?.downloadBookletErrorMessageImage,
                                'download-booklet-error-icon'
                            )}
                    </div>
                )}

                {requestState === 'SUCCESS' && packageURL && (
                    <div className={'booklet-success'}>
                        <a
                            data-testid="booklet-download-link"
                            href={packageURL}
                            target="_blank"
                            rel="noreferrer"
                            className="download-booklet-link"
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(rest.downloadLinkText, data),
                            }}
                        />
                        <span
                            className="download-booklet-text"
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(rest.downloadBookletText, data),
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DownloadBooklet;
